'use client';

import type { ClassifiedQuery, FeedItem, StageState } from '@/app/d/search/types';

/**
 * Client-side helpers for the unified-search save lifecycle. These wrap the
 * exact same endpoints `/d/search` uses, so any page (regular search, Simple
 * Search) persists and restores a search identically — same searchId, same
 * `{ query, classified, feed }` snapshot, same promotion-to-Report path. The
 * only thing that differs between pages is how the feed is *visualized*.
 */

export interface SearchSnapshotPayload {
  query: string;
  classified: ClassifiedQuery | null;
  stages: StageState;
  feed: FeedItem[];
}

export interface LoadedSearch {
  query: string;
  classified: ClassifiedQuery | null;
  feed: FeedItem[];
  /** Present only when restoring from the live cache (not a promoted Report). */
  stages?: StageState;
}

export interface EvaluateResult {
  promoted?: boolean;
  searchId?: string;
}

/** Create the server-side cache entry for a brand-new search (fire-and-forget). */
export function initSearchCache(searchId: string, query: string): void {
  void fetch('/api/search/init', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ searchId, query }),
  }).catch(() => {});
}

/** Persist the current snapshot to the cache (fire-and-forget). */
export function putSearchSnapshot(searchId: string, payload: SearchSnapshotPayload): void {
  void fetch(`/api/search/${searchId}/snapshot`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

/**
 * Evaluate the finished search for promotion to a saved Report. With
 * `autoSave: true` it promotes directly; otherwise the server runs its quality
 * eval. Returns the (possibly new) searchId so the caller can adopt it.
 */
export async function evaluateSearch(params: {
  searchId: string;
  query: string;
  classified: ClassifiedQuery | null;
  feed: FeedItem[];
  autoSave?: boolean;
}): Promise<EvaluateResult | null> {
  try {
    const res = await fetch('/api/search/evaluate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        searchId: params.searchId,
        query: params.query,
        classified: params.classified,
        feed: params.feed,
        autoSave: params.autoSave ?? false,
      }),
    });
    return res.ok ? ((await res.json()) as EvaluateResult) : null;
  } catch {
    return null;
  }
}

/** Load a saved search by id (Report for authed users, else 7-day cache). */
export async function loadSearch(searchId: string): Promise<LoadedSearch | null> {
  try {
    const res = await fetch(`/api/search/${searchId}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.content) return null;
    const content = data.content as {
      query: string;
      classified: ClassifiedQuery | null;
      feed: FeedItem[];
    };
    return {
      query: content.query,
      classified: content.classified ?? null,
      feed: Array.isArray(content.feed) ? content.feed : [],
      stages: data.stages ?? undefined,
    };
  } catch {
    return null;
  }
}
